/*  MUI  */
import { Box, Grid, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { SaaSHealthLineGraph } from 'application/Shared/Components/Reports/SaaSHealthLineGraph';
import { QBRFIRSTREPORTS } from 'application/Shared/Constants';
import { Route, dateTimeString } from 'application/Shared/Helpers';
import { Insights, Notes } from 'application/Shared/Pages';

/*  NPM  */
import { endOfQuarter, startOfQuarter } from 'date-fns';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Components  */
import { QBRFirstAppOverlapScore } from './internal/QBRFirstAppOverlapScore';
import { QBRFirstApplicationClassificationResults } from './internal/QBRFirstApplicationClassificationResults';
import { QBRFirstManagedAppScore } from './internal/QBRFirstManagedAppScore';
import { QBRFirstScoreOverview } from './internal/QBRFirstScoreOverview';
import { QBRFirstShadowItScore } from './internal/QBRFirstShadowItScore';
import { QBRFirstUnapprovedAppScore } from './internal/QBRFirstUnapprovedAppScore';
import { QBRScoreChip } from './internal/QBRScoreChip';

/*  Constants  */
const scoreOverviewReport = 'qbrQBRFirstScoreOverview';

/*  Styles  */
const sxs = {
    default: { margin: { marginLeft: 0, marginRight: 0 } }
};

const RenderFirstScoreOverviewWithGraph = (props) => {
    const { t } = useTranslation();
    const { height, saasHealthLineGraphOnly } = props;

    return (
        <Grid
            container
            data-testid="qbrRenderFirstScoreOverviewWithGraph"
            spacing={3}
        >
            <Grid item xs={12} lg={saasHealthLineGraphOnly ? 12 : 8}>
                <Paper
                    variant="outlined"
                    style={{ padding: '16px' }}
                    data-testid="qbrRenderFirstScoreOverviewSaaSGraph"
                >
                    {saasHealthLineGraphOnly && (
                        <>
                            <Typography color="textSecondary" variant="h4">
                                {t('Common.health_score')}
                            </Typography>
                            <hr />
                        </>
                    )}
                    <SaaSHealthLineGraph
                        {...props}
                        height={saasHealthLineGraphOnly ? height : 462}
                    />
                </Paper>
            </Grid>
            {!saasHealthLineGraphOnly && (
                <Grid
                    item
                    xs={12}
                    lg={4}
                    data-testid="qbrRenderFirstScoreOverviewScores"
                >
                    <QBRFirstScoreOverview {...props} withGraph />
                </Grid>
            )}
        </Grid>
    );
};

const RenderScoreDetails = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { reportData } = props;

    const Scores = Object.keys(QBRFIRSTREPORTS).map((reportType) => {
        const reportKey = QBRFIRSTREPORTS[reportType].key;

        if (reportType === 'SaaSSophisticationScore') {
            return <span key="renderScoreDetails_SaaSSophisticationScore" />;
        }

        if (!reportData[reportKey]) {
            return null;
        }

        const score = reportData[reportKey].score || 0;

        return (
            <Box
                key={`renderScoreDetails_${reportType}`}
                data-testid="qbrRenderScoreDetails"
                display="flex"
                flexDirection="column"
            >
                <Typography
                    variant="p"
                    style={{
                        color: theme.palette.qbrFirstReports[reportType],
                        fontWeight: 600
                    }}
                >
                    {t(QBRFIRSTREPORTS[reportType].name)}:
                    <QBRScoreChip score={score} />
                </Typography>
                <Typography variant="p">
                    {t(`QBRFirst.definitions.${reportType}`)}
                </Typography>
            </Box>
        );
    });

    return <Box paddingY="32px">{Scores}</Box>;
};

const RenderInsights = (props) => {
    const { t } = useTranslation();
    const { clientId, partnerId } = useParams();
    const { reportSetup } = props;
    const { endTime, period, platformTenantId, startTime } = reportSetup;
    const baseAdminRoute = Route.buildBaseAdminRoute({ clientId, partnerId });

    const start =
        period === 'quarterly'
            ? startOfQuarter(new Date(startTime))
            : new Date(startTime);
    const end =
        period === 'quarterly'
            ? endOfQuarter(new Date(endTime))
            : new Date(endTime);

    const dateSearch = {
        field: 'dateCreated',
        value: `{"between": "${dateTimeString(start)}, ${dateTimeString(end)}"}`
    };

    return (
        <Box
            paddingY="32px"
            style={{ pageBreakBefore: 'always' }}
            data-testid="qbrRenderInsights"
        >
            <Typography variant="h1">{t('Common.insights')}</Typography>
            <Typography
                style={{
                    ...sxs.default.margin,
                    marginTop: '12px'
                }}
            >
                {t('QBR.definitions.insights')}
            </Typography>
            <hr />
            <Box
                style={{
                    ...sxs.default.margin
                }}
            >
                <Insights
                    clientId={platformTenantId}
                    dateSearch={dateSearch}
                    link={clientId ? `${baseAdminRoute}/insights` : null}
                    report
                />
            </Box>
        </Box>
    );
};

const RenderNotes = (props) => {
    const { t } = useTranslation();
    const { reportSetup } = props;
    const { platformTenantId } = reportSetup;

    return (
        <Box
            paddingY="32px"
            style={{ pageBreakBefore: 'always' }}
            data-testid="qbrRenderNotes"
        >
            <Typography variant="h1">
                {t('Common.notes_and_recommendations')}
            </Typography>
            <Typography
                style={{
                    ...sxs.default.margin,
                    marginTop: '12px'
                }}
            >
                {t('QBR.definitions.notes')}
            </Typography>
            <hr />
            <Box style={sxs.default.margin}>
                <Notes clientId={platformTenantId} report />
            </Box>
        </Box>
    );
};

export const QBRFirstReports = (props) => {
    const { reportSetup, reportData, renderingScheduledReport } = props;

    const { t } = useTranslation();

    if (typeof reportData === 'undefined') {
        return null;
    }

    // This is for the score cards in the Admin Dashboard for the Client
    if (reportSetup?.reportSelections?.includes(scoreOverviewReport)) {
        return <QBRFirstScoreOverview {...props} />;
    }

    if (
        reportSetup?.reportSelections?.includes(
            `${scoreOverviewReport}AndGraph`
        )
    ) {
        return <RenderFirstScoreOverviewWithGraph {...props} />;
    }

    const { reportSelections } = reportSetup;

    const scoreMap = {};

    Object.entries(reportData).forEach(([key, value]) => {
        scoreMap[key] = value.score;
    });

    return (
        <Box width="100" position="relative" data-testid="qbrMainRender">
            {reportSelections.includes(
                'qbrQBRFirstSaaSSophisticationScore'
            ) && (
                <Box
                    data-testid="qbrMainRenderSaaSHealthLineGraphWithScoreDetails"
                    id="qbrQBRFirstSaaSSophisticationScore"
                >
                    <Box paddingY="32px">
                        <Box
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Typography variant="h1">
                                {t(
                                    QBRFIRSTREPORTS.SaaSSophisticationScore.name
                                )}
                                :
                            </Typography>
                            <QBRScoreChip
                                score={
                                    scoreMap.qbrQBRFirstSaaSSophisticationScore
                                }
                                renderType="saasHealthHeader"
                            />
                        </Box>
                        <Typography
                            style={{
                                marginBottom: '32px',
                                marginTop: '12px'
                            }}
                        >
                            {t('QBRFirst.definitions.SaaSSophisticationScore')}
                        </Typography>
                        <SaaSHealthLineGraph
                            {...props}
                            renderingScheduledReport={renderingScheduledReport}
                        />
                    </Box>
                    <RenderScoreDetails {...props} />
                </Box>
            )}
            {reportSelections.includes('qbrQBRFirstInsights') && (
                <Box id="qbrQBRFirstInsights">
                    <RenderInsights {...props} />
                </Box>
            )}
            {reportSelections.includes('qbrQBRFirstNotes') && (
                <Box id="qbrQBRFirstNotes">
                    <RenderNotes {...props} />
                </Box>
            )}
            {reportData.qbrQBRFirstAppClassificationScore && (
                <Box
                    paddingY="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstApplicationClassificationResults"
                    id="qbrQBRFirstAppClassificationScore"
                >
                    <QBRFirstApplicationClassificationResults
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={
                            reportData.qbrQBRFirstAppClassificationScore
                        }
                    />
                </Box>
            )}
            {reportData.qbrQBRFirstManagedAppScore && (
                <Box
                    paddingY="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstManagedAppScore"
                    id="qbrQBRFirstManagedAppScore"
                >
                    <QBRFirstManagedAppScore
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.qbrQBRFirstManagedAppScore}
                    />
                </Box>
            )}
            {reportData.qbrQBRFirstShadowITScore && (
                <Box
                    paddingY="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstShadowItScore"
                    id="qbrQBRFirstShadowITScore"
                >
                    <QBRFirstShadowItScore
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.qbrQBRFirstShadowITScore}
                    />
                </Box>
            )}
            {reportData.qbrQBRFirstUnapprovedAppScore && (
                <Box
                    paddingY="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstUnapprovedAppScore"
                    id="qbrQBRFirstUnapprovedAppScore"
                >
                    <QBRFirstUnapprovedAppScore
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.qbrQBRFirstUnapprovedAppScore}
                    />
                </Box>
            )}
            {reportData.qbrQBRFirstAppOverlapScore && (
                <Box
                    paddingY="32px"
                    style={{ pageBreakBefore: 'always' }}
                    data-testid="qbrMainRenderQBRFirstAppOverlapScore"
                    id="qbrQBRFirstAppOverlapScore"
                >
                    <QBRFirstAppOverlapScore
                        {...props}
                        scoreMap={scoreMap}
                        renderingScheduledReport={renderingScheduledReport}
                        reportSetup={reportSetup}
                        reportData={reportData.qbrQBRFirstAppOverlapScore}
                    />
                </Box>
            )}
        </Box>
    );
};
